import Cookies from 'js-cookie';
import { DEFAULT_CONNECTION, type ConnectionInfo } from '@/components/shell/simple/constants';
import { IS_BFF, getCsrfToken } from '@/lib/api/auth-mode';

const COOKIE_NAME = process.env.NEXT_PUBLIC_COOKIE_NAME || process.env.COOKIE_NAME || 'wexa_dev_cookie';

/** Current session token — cookie first (live), then the localStorage cache. */
function sessionToken(): string | null {
  const cookie = Cookies.get(COOKIE_NAME);
  if (cookie) return cookie;
  if (typeof window !== 'undefined') return localStorage.getItem('auth_token');
  return null;
}

/**
 * Wexa Fabric MCP / API Gateway client.
 *
 * `GET /v1/connection-info` is public (no auth, no secrets) — it returns the
 * MCP/REST/SDK coordinates configured in the gateway's env. Reached same-origin
 * via the `/gateway-api` proxy (see app/gateway-api/[...path]/route.ts).
 */

/** Raw wire shape from the gateway (snake_case). */
interface RawConnectionInfo {
  mcp?: { url_template?: string; url?: string; sse_url?: string; auth?: string };
  api?: { base_url?: string };
  sdk?: {
    python?: { package?: string; install?: string };
    node?: { package?: string; install?: string };
  };
  workspace_header?: string;
  issuer?: string;
  tools?: string[];
}

/** Map the wire shape to the UI's ConnectionInfo, defaulting any missing field. */
function normalize(raw: RawConnectionInfo): ConnectionInfo {
  const d = DEFAULT_CONNECTION;
  return {
    mcpServerUrl: raw.mcp?.url_template || raw.mcp?.url || d.mcpServerUrl,
    mcpSseUrl: raw.mcp?.sse_url || d.mcpSseUrl,
    apiBaseUrl: raw.api?.base_url || d.apiBaseUrl,
    workspaceHeader: raw.workspace_header || d.workspaceHeader,
    issuer: raw.issuer || (raw.api?.base_url || d.apiBaseUrl).replace(/\/v1$/, ''),
    sdk: {
      python: {
        package: raw.sdk?.python?.package || d.sdk.python.package,
        install: raw.sdk?.python?.install || d.sdk.python.install,
      },
      ...(raw.sdk?.node?.install
        ? { node: { package: raw.sdk.node.package ?? '', install: raw.sdk.node.install } }
        : {}),
    },
    tools: raw.tools ?? d.tools,
  };
}

/** A key's own read profile. Restrict-only: it can drop what its creator holds, never add. */
export interface ApiKeyDataAccess {
  /** Clearances such as `detail:pii`, `detail:financial`. `detail:internal` is refused. */
  detail: string[];
  /** Bare audience ids or `part:<id>`. */
  audiences?: string[];
}

export interface CreateApiKeyParams {
  orgId: string;
  deptId?: string;
  projectId: string;
  grants?: string[];
  /** Optional human-readable label (e.g. "LangGraph test key") — never derived from or related to the secret. */
  name?: string;
  /** Optional per-key read profile. Omit to read whatever the creator reads. */
  dataAccess?: ApiKeyDataAccess;
}

/** Build the POST /v1/apikeys body. `data_access` is sent only when set. */
export function buildCreateApiKeyBody(p: CreateApiKeyParams) {
  return {
    org_id: p.orgId,
    dept_id: p.deptId,
    project_id: p.projectId,
    grants: p.grants ?? [],
    name: p.name,
    ...(p.dataAccess ? { data_access: p.dataAccess } : {}),
  };
}

export interface CreatedApiKey {
  id: string;
  secret: string; // fab_sk_… — shown exactly once
  name?: string;
}

/** A previously-minted key as listed back — never includes the secret. */
export interface ApiKeySummary {
  id: string;
  name?: string;
  created_by: string;
  created_at: string;
  last_used_at?: string;
  revoked: boolean;
  /** The key's pinned scope. `data_access` is absent for keys that read as their creator. */
  scope?: { data_access?: ApiKeyDataAccess | null };
}

export interface SdkSessionSummary {
  id: string;
  user_id: string;
  email?: string;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
  grants: string[];
}

/** Shared auth headers for the API key management calls below. */
function apiKeyAuthHeaders(): { headers: Record<string, string>; credentials: RequestCredentials } {
  const token = IS_BFF ? null : sessionToken();
  if (!IS_BFF && !token) throw new Error('Sign in to manage API keys.');
  return {
    credentials: IS_BFF ? 'include' : 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(IS_BFF
        ? (getCsrfToken() ? { 'x-csrf-token': getCsrfToken() as string } : {})
        : { Authorization: `Bearer ${token}` }),
    },
  };
}

export const gatewayService = {
  async getConnectionInfo(signal?: AbortSignal): Promise<ConnectionInfo> {
    const res = await fetch('/gateway-api/v1/connection-info', {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) throw new Error(`connection-info responded ${res.status}`);
    return normalize((await res.json()) as RawConnectionInfo);
  },

  /**
   * Mint a project-scoped API key (`fab_sk_…`). Requires the signed-in user's
   * session JWT (admin role on the project) — the gateway validates it and
   * derives the role from the token. The secret is returned once and is never
   * retrievable again.
   */
  async createApiKey(p: CreateApiKeyParams): Promise<CreatedApiKey> {
    // bff mode: no JS token — the BFF injects Authorization from the httpOnly
    // cookie. Send cookies + CSRF header instead of a Bearer.
    const token = IS_BFF ? null : sessionToken();
    if (!IS_BFF && !token) throw new Error('Sign in to generate an API key.');

    const res = await fetch('/gateway-api/v1/apikeys', {
      method: 'POST',
      credentials: IS_BFF ? 'include' : 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(IS_BFF
          ? (getCsrfToken() ? { 'x-csrf-token': getCsrfToken() as string } : {})
          : { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(buildCreateApiKeyBody(p)),
    });

    if (res.status === 401) throw new Error('Your session isn’t accepted by the gateway. Re-authenticate and try again.');
    if (res.status === 403) throw new Error('An admin role on this project is required to generate an API key.');
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.json())?.error_description || '';
      } catch {
        /* non-JSON error body — fall through to status */
      }
      throw new Error(detail || `Key generation failed (${res.status}).`);
    }

    const data = (await res.json()) as { id: string; secret: string; name?: string };
    return { id: data.id, secret: data.secret, name: data.name };
  },

  /** List a project's API keys (secrets are never returned — shown once, at creation). */
  async listApiKeys(projectId: string): Promise<ApiKeySummary[]> {
    const { headers, credentials } = apiKeyAuthHeaders();
    const res = await fetch(`/gateway-api/v1/apikeys?project_id=${encodeURIComponent(projectId)}`, {
      credentials,
      headers,
    });
    if (res.status === 401) throw new Error('Your session isn’t accepted by the gateway. Re-authenticate and try again.');
    if (res.status === 403) throw new Error('An admin role on this project is required to view API keys.');
    if (!res.ok) throw new Error(`Listing API keys failed (${res.status}).`);
    const data = (await res.json()) as { keys: ApiKeySummary[] };
    return data.keys ?? [];
  },

  /** Revoke a key by ID. Immediate and irreversible — the key stops working right away. */
  async revokeApiKey(id: string, projectId: string): Promise<void> {
    const { headers, credentials } = apiKeyAuthHeaders();
    const res = await fetch(
      `/gateway-api/v1/apikeys/${encodeURIComponent(id)}?project_id=${encodeURIComponent(projectId)}`,
      { method: 'DELETE', credentials, headers },
    );
    if (res.status === 401) throw new Error('Your session isn’t accepted by the gateway. Re-authenticate and try again.');
    if (res.status === 403) throw new Error('An admin role on this project is required to revoke API keys.');
    if (res.status === 404) throw new Error('That key no longer exists in this project.');
    if (!res.ok) throw new Error(`Revoking the key failed (${res.status}).`);
  },

  /** List a project's active SDK sessions (users signed in through the SDK). */
  async listSdkSessions(projectId: string): Promise<SdkSessionSummary[]> {
    const { headers, credentials } = apiKeyAuthHeaders();
    const res = await fetch(`/gateway-api/v1/sdk-sessions?project_id=${encodeURIComponent(projectId)}`, {
      credentials,
      headers,
    });
    if (res.status === 401) throw new Error('Your session isn’t accepted by the gateway. Re-authenticate and try again.');
    if (res.status === 403) throw new Error('An admin role on this project is required to view SDK sessions.');
    if (!res.ok) throw new Error(`Listing SDK sessions failed (${res.status}).`);
    const data = (await res.json()) as { sessions: SdkSessionSummary[] };
    return data.sessions ?? [];
  },

  /** Revoke one SDK session. Its refresh token stops working at once. */
  async revokeSdkSession(id: string, projectId: string): Promise<void> {
    const { headers, credentials } = apiKeyAuthHeaders();
    const res = await fetch(
      `/gateway-api/v1/sdk-sessions/${encodeURIComponent(id)}?project_id=${encodeURIComponent(projectId)}`,
      { method: 'DELETE', credentials, headers },
    );
    if (res.status === 401) throw new Error('Your session isn’t accepted by the gateway. Re-authenticate and try again.');
    if (res.status === 403) throw new Error('An admin role on this project is required to revoke SDK sessions.');
    if (res.status === 404) throw new Error('That SDK session no longer exists in this project.');
    if (!res.ok) throw new Error(`Revoking the SDK session failed (${res.status}).`);
  },

  /** End every active SDK session of one user in a project. Returns how many ended. */
  async revokeSdkSessionsForUser(userId: string, projectId: string): Promise<number> {
    const { headers, credentials } = apiKeyAuthHeaders();
    const res = await fetch(`/gateway-api/v1/sdk-sessions/revoke-user?project_id=${encodeURIComponent(projectId)}`, {
      method: 'POST',
      credentials,
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });
    if (res.status === 401) throw new Error('Your session isn’t accepted by the gateway. Re-authenticate and try again.');
    if (res.status === 403) throw new Error('An admin role on this project is required to end SDK sessions.');
    if (!res.ok) throw new Error(`Ending the user’s SDK sessions failed (${res.status}).`);
    const data = (await res.json()) as { revoked?: number };
    return data.revoked ?? 0;
  },

  /** List the signed-in user's own active SDK sessions in a project. */
  async listMySdkSessions(projectId: string): Promise<SdkSessionSummary[]> {
    const { headers, credentials } = apiKeyAuthHeaders();
    const res = await fetch(`/gateway-api/v1/sdk-sessions/mine?project_id=${encodeURIComponent(projectId)}`, {
      credentials,
      headers,
    });
    if (res.status === 401) throw new Error('Your session isn’t accepted by the gateway. Re-authenticate and try again.');
    if (!res.ok) throw new Error(`Listing your SDK sessions failed (${res.status}).`);
    const data = (await res.json()) as { sessions: SdkSessionSummary[] };
    return data.sessions ?? [];
  },

  /** End one of the signed-in user's own SDK sessions. */
  async revokeMySdkSession(id: string, projectId: string): Promise<void> {
    const { headers, credentials } = apiKeyAuthHeaders();
    const res = await fetch(
      `/gateway-api/v1/sdk-sessions/mine/${encodeURIComponent(id)}?project_id=${encodeURIComponent(projectId)}`,
      { method: 'DELETE', credentials, headers },
    );
    if (res.status === 401) throw new Error('Your session isn’t accepted by the gateway. Re-authenticate and try again.');
    if (res.status === 404) throw new Error('That session has already ended.');
    if (!res.ok) throw new Error(`Ending the session failed (${res.status}).`);
  },
};
